// Pure derivations over the `GET /api/organizer/beauty/stats` response.
//
// The back does the accounting (see its README, "Estadísticas de Beauty (panel)"); this module only
// reshapes that response for display: flat metrics, chart series, insights, filter options and the
// request query. Ratios with an empty denominator stay `null` all the way down so the UI shows a
// dash instead of a misleading 0. The court screen has its own sibling, courtStatsMetrics.js; the
// pieces both screens need (heat grid, series helpers, formatting) live in the shared stats*.js files.

import { channelShares as sharedChannelShares } from "./statsChannels";
import { TREND_METRICS } from "./statsChart";
import { daysInRange } from "./statsDates";
import { buildHeatGrid as sharedBuildHeatGrid, heatExtremes, weekdayPlural } from "./statsHeat";
import { fmtInt, fmtMoney, fmtPct, fmtPct0, hourRange } from "./statsFormat";

const minutesToHours = (m) => (typeof m === "number" ? m / 60 : null);

/**
 * Flat metrics of one response. `perDay` averages the appointments over the days of the range.
 * @returns {object|null} null when there is no response yet.
 */
export const deriveMetrics = (info) => {
  const t = info?.totals;
  if (!t) return null;
  const days = daysInRange({ from: info.from, to: info.to });
  return {
    occupancy: t.occupancy,
    revenue: t.revenueCollected?.total ?? null,
    revenueOnline: t.revenueCollected?.online ?? null,
    revenuePos: t.revenueCollected?.pos ?? null,
    reversed: t.revenueCollected?.reversed ?? null,
    appointments: t.appointments,
    attended: t.attended,
    perDay: t.appointments / days,
    ticket: t.ticketAverage,
    servicesPerAppointment: t.servicesPerAppointment,
    cancellationRate: t.cancellationRate,
    noShowRate: t.noShowRate,
    cancelled: t.cancelled,
    noShows: t.noShows,
    pending: t.pending,
    noShowUncollected: t.noShowUncollected,
    freeHours: t.freeHours,
    agendaHours: minutesToHours(t.agendaMinutes),
    bookedHours: minutesToHours(t.bookedMinutes),
    viaLinkShare: t.viaLinkShare,
    linkCount: info.choice?.link ?? 0,
    avgLeadHours: t.avgLeadHours,
    sameDayShare: t.sameDayShare,
    commission: t.commission,
  };
};

/**
 * Chart series from the zero-filled `daily` array, grouped in buckets of `bucket` days.
 * - occ: booked / agenda minutes of the bucket (null when nobody had agenda).
 * - ticket: revenue per appointment of the bucket (null when it has no appointments).
 * - rev / res: daily average inside the bucket, so a short last bucket is not understated.
 *
 * @param {Array} daily
 * @param {"occ"|"ticket"|"rev"|"res"} metric
 * @param {number} bucket  1 (daily) or 7 (weekly)
 * @returns {Array<{index: number, date: string, value: number|null}>}  `date` is the bucket's first day.
 */
export const buildSeries = (daily, metric, bucket) => {
  const points = [];
  for (let i = 0; i < daily.length; i += bucket) {
    const slice = daily.slice(i, i + bucket);
    const sum = (key) => slice.reduce((acc, d) => acc + d[key], 0);
    let value;
    if (metric === "occ") {
      const agenda = sum("agendaMinutes");
      value = agenda ? sum("bookedMinutes") / agenda : null;
    } else if (metric === "ticket") {
      const appointments = sum("appointments");
      value = appointments ? sum("revenue") / appointments : null;
    } else if (metric === "rev") value = sum("revenue") / slice.length;
    else value = sum("appointments") / slice.length;
    points.push({ index: points.length, date: slice[0].date, value });
  }
  return points;
};

/* ------------------------------ weekday x hour heat ------------------------------ */

/** Weekday x hour grid of the professionals' agenda (see statsHeat.js); hours come from the data. */
export const buildHeatGrid = (info) => sharedBuildHeatGrid(info, "agendaMinutes");

/* ------------------------------------ insights ------------------------------------ */

// A slot under this share of its agenda counts as quiet (Beauty agendas are looser than courts').
const QUIET_SLOT = 0.35;

/**
 * Up to three cards under the KPI tiles. `parts` alternate plain text and bold text (odd indexes
 * are bold), so the component can render them without HTML strings.
 *
 * 1. Peak slot, 2. quietest slot (and how many slots sit under 35 %), 3. the service that bills the
 * most. Nothing is shown when the period has no appointments: a "peak" of 0 % is not an insight.
 */
export const selectInsights = (info) => {
  if (!info?.totals || !info.totals.appointments) return [];

  const { peak, low, lowCount } = heatExtremes(buildHeatGrid(info), QUIET_SLOT);
  const cards = [];
  if (peak) {
    cards.push({
      key: "peak",
      eyebrow: "Horario pico",
      tone: "peak",
      parts: [
        "Los ",
        `${weekdayPlural(peak.weekday)} de ${hourRange(peak.hour)}`,
        " tu agenda está ",
        `${fmtPct0(peak.value)} ocupada`,
        ". Conviene abrir más horas o sumar una profesional ese día.",
      ],
    });
  }
  if (low) {
    cards.push({
      key: "valley",
      eyebrow: "Horas valle",
      tone: "valley",
      parts: [
        "Hay ",
        `${lowCount} ${lowCount === 1 ? "franja" : "franjas"} por semana`,
        " por debajo del 35 % de ocupación. La más floja: ",
        `${weekdayPlural(low.weekday)} de ${hourRange(low.hour)} (${fmtPct0(low.value)})`,
        ". Buen horario para una promo de semana.",
      ],
    });
  }

  const top = (info.services || []).filter((s) => s.count > 0).sort((a, b) => b.revenue - a.revenue)[0];
  if (top && top.revenue > 0) {
    cards.push({
      key: "topService",
      eyebrow: "Servicio que más factura",
      tone: "ink",
      parts: ["", top.name, ` (${top.category}) facturó `, fmtMoney(top.revenue), `, el ${fmtPct0(top.share)} del total, en ${top.count} turnos.`],
    });
  }
  return cards;
};

/* ------------------------------------ filters ------------------------------------ */

/** Options of the Profesional filter: the salon's professionals that are not deactivated. */
export const professionalFilterOptions = (professionals) =>
  (professionals || [])
    .filter((p) => p.active !== false && p.status !== false)
    .map((p) => ({ value: p._id, label: p.name }));

/** Query for the stats endpoint: the range, plus professional / salon only when set ("all" = none). */
export const buildStatsParams = (range, professionalId, salonId) => ({
  from: range.from,
  to: range.to,
  ...(professionalId && professionalId !== "all" ? { professionalId } : {}),
  ...(salonId ? { salonId } : {}),
});

/** True when the back scoped the response to the linked professional's own numbers. */
export const isLinkedScope = (info) => info?.scope?.type === "own";

/* ------------------------------ trend, channels and collections ------------------------------ */

/** Metrics of the trend chart: like the court screen's, but the booking count is "Turnos". */
export const BEAUTY_TREND_METRICS = { ...TREND_METRICS, res: { ...TREND_METRICS.res, label: "Turnos" } };

/** One entry per channel (Web, App, Mostrador / carga manual) with its share of the appointments. */
export const channelShares = (channels) => sharedChannelShares(channels, "appointments");

/**
 * How clients came to the professional, one row each (the back puts every appointment in exactly one):
 * through a professional's own link, choosing a professional on the web or app, without preference
 * (the system assigned one) or loaded at the counter. `share` is over all of them (null when none).
 */
export const choiceRows = (choice) => {
  const rows = [
    { key: "link", label: "Por el link del profesional", count: choice?.link ?? 0 },
    { key: "chose", label: "Eligió profesional en la web o app", count: choice?.chose ?? 0 },
    { key: "any", label: "Sin preferencia (asignó el sistema)", count: choice?.any ?? 0 },
    { key: "desk", label: "Cargados en el mostrador", count: choice?.desk ?? 0 },
  ];
  const total = rows.reduce((acc, r) => acc + r.count, 0);
  return rows.map((r) => ({ ...r, share: total ? r.count / total : null }));
};

/** What was billed in the period: collected online, collected at the counter, still pending. */
export const collectionRows = (totals) => {
  const rows = [
    { key: "online", label: "Cobrado online (seña)", amount: totals.revenueCollected.online },
    { key: "pos", label: "Cobrado en mostrador (ZocoPDV)", amount: totals.revenueCollected.pos },
    { key: "pending", label: "Pendiente de cobro", amount: totals.pending },
  ];
  const billed = rows.reduce((acc, r) => acc + r.amount, 0);
  return rows.map((r) => ({ ...r, share: billed ? r.amount / billed : null }));
};

/* --------------------------------------- per professional --------------------------------------- */

/**
 * Table rows from `perProfessional`: hours with appointments, the no-show rate and the share of the
 * professional's appointments that came through their own link (both null when they had none).
 */
export const buildProfessionalRows = (perProfessional) =>
  (perProfessional || []).map((p) => ({
    id: p.id,
    name: p.name,
    specialty: p.specialty || "",
    appointments: p.appointments,
    bookedHours: p.bookedMinutes / 60,
    occupancy: p.occupancy,
    revenue: p.revenue,
    ticket: p.ticketAverage,
    commission: p.commission,
    noShowRate: p.appointments > 0 ? p.noShows / p.appointments : null,
    linkShare: p.appointments > 0 ? p.viaLink / p.appointments : null,
  }));

/* ------------------------------------------ per service ------------------------------------------ */

export const ALL_CATEGORIES = "all";

const list = (services) => (Array.isArray(services) ? services : []);

/** Chips of the category filter: "Todas" plus one per category with services done, in order of first appearance. */
export const serviceCategories = (services) => {
  const seen = [];
  list(services).forEach((s) => {
    if (s.count > 0 && !seen.includes(s.category)) seen.push(s.category);
  });
  return [{ value: ALL_CATEGORIES, label: "Todas" }, ...seen.map((category) => ({ value: category, label: category }))];
};

/**
 * Rows of the bars and the table: the services that were done, optionally narrowed to a category,
 * highest revenue first. `share` is the back's share of the salon's TOTAL billed by services, so it
 * does not change when a category chip is selected.
 */
export const buildServiceRows = (services, category = ALL_CATEGORIES) =>
  list(services)
    .filter((s) => s.count > 0 && (category === ALL_CATEGORIES || s.category === category))
    .map((s) => ({
      key: s.serviceId || s.name,
      name: s.name,
      category: s.category,
      count: s.count,
      revenue: s.revenue,
      avgPrice: s.avgPrice,
      durationMinutes: s.durationMinutes,
      share: s.share,
    }))
    .sort((a, b) => b.revenue - a.revenue);

export const durationText = (minutes) => (minutes > 0 ? `${minutes} min` : "—");

/** Text twin of the bars ("Ver tabla"): header and already formatted cells. */
export const servicesTable = (rows) => ({
  headers: ["Servicio", "Categoría", "Veces", "Facturado", "Precio promedio", "Duración", "% del total"],
  rows: rows.map((r) => [r.name, r.category, fmtInt(r.count), fmtMoney(r.revenue), fmtMoney(r.avgPrice), durationText(r.durationMinutes), fmtPct(r.share)]),
});

